/**
 * Booking payment/status timeline — derived purely from the booking's live
 * state so it AUTO-UPDATES (no separate event log needed). Mirrors the money
 * truth used everywhere via getPaymentState (amount-driven).
 *
 * Steps surfaced on the operator booking card:
 *   Created → [Deposit paid] → Paid in full → [Waiver signed] → Completed
 * (bracketed steps only show when relevant). Pre-booking funnel events
 * (interest / Stripe attempt) live in the Relations timeline, not here.
 */
import { getPaymentState, PAID_FORGIVENESS } from './payment';

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

/** @returns {{cancelled:boolean, steps:Array, pay:object}} */
export function getBookingTimeline(b) {
  if (!b) return { cancelled: false, steps: [], pay: { state: 'unpaid', total: 0, paid: 0, remaining: 0 } };

  const pay = getPaymentState(b);
  const status = String(b.status ?? b.bookingStatus ?? b.booking_status ?? '').toLowerCase();
  const payStatus = String(b.paymentStatus ?? b.payment_status ?? '').toLowerCase();

  const createdAt = b.createdAt ?? b.created_at ?? null;
  const paidAt = b.paidAt ?? b.paid_at ?? null;
  // Stamped when the deposit landed (paid_at gets overwritten by the balance
  // payment, so this preserves the TRUE deposit date on the timeline).
  const depositPaidAt = b.depositPaidAt ?? b.deposit_paid_at ?? null;
  const waiverSignedAt = b.waiverSignedAt ?? b.waiver_signed_at ?? null;
  const waiverSigned = Boolean(b.waiverSigned ?? b.waiver_signed);
  const depositPaid = num(b.depositPaid ?? b.deposit_paid);
  const checkedIn = Boolean(b.checkedIn ?? b.checked_in);
  const bookingDate = b.date ?? b.bookingDate ?? b.booking_date ?? null;

  const cancelled = status === 'cancelled' || status === 'canceled';
  const hasMoney = pay.total > 0;
  const isDeposit =
    pay.state === 'deposit' ||
    depositPaid > PAID_FORGIVENESS ||
    ['deposit_paid', 'partial', 'partially_paid'].includes(payStatus);
  const fullyPaid = pay.state === 'paid' && pay.paid > PAID_FORGIVENESS;

  // Trip date already in the past → treat as completed (auto-updates daily).
  let datePassed = false;
  if (bookingDate) {
    const d = new Date(bookingDate);
    if (!isNaN(d)) datePassed = d < new Date(new Date().toDateString());
  }
  const completed = status === 'completed' || checkedIn || datePassed;

  const steps = [];
  steps.push({ key: 'created', label: 'Created', done: true, at: createdAt });

  if (isDeposit) {
    steps.push({
      key: 'deposit',
      label: 'Deposit paid',
      done: pay.paid > PAID_FORGIVENESS,
      // True deposit date; for legacy bookings without the stamp, fall back to
      // created (deposit is collected at booking) once fully paid — never show
      // the balance-payment date on the deposit step.
      at: depositPaidAt || (fullyPaid ? createdAt : paidAt),
      amount: depositPaid > PAID_FORGIVENESS ? depositPaid : pay.paid,
    });
  }

  if (hasMoney) {
    steps.push({
      key: 'paid',
      // When a balance remains, surface it right in the step label. Once a
      // deposit booking settles, the final payment reads "Balance paid".
      label: fullyPaid
        ? (isDeposit ? 'Balance paid' : 'Paid in full')
        : (pay.remaining > PAID_FORGIVENESS ? `Owes $${pay.remaining.toFixed(2)}` : 'Paid in full'),
      done: fullyPaid,
      warn: !fullyPaid && pay.remaining > PAID_FORGIVENESS,
      at: fullyPaid ? paidAt : null,
    });
  }

  if (waiverSigned) {
    steps.push({ key: 'waiver', label: 'Waiver signed', done: true, at: waiverSignedAt });
  }

  steps.push({
    key: 'completed',
    label: completed ? 'Completed' : 'Upcoming',
    done: completed,
    at: completed && datePassed ? bookingDate : null,
  });

  return { cancelled, steps, pay };
}
